"use client";

import { Button } from "@cmp-src/primitives/button";
import { Slider } from "@cmp-src/primitives/slider";
import type {
  ReportAccessMode,
  ReportCatalog,
  ReportGoodWhen,
  ReportLiveMapResponse,
  ReportLiveState,
  ReportMapPin,
  ReportMapResource,
  ReportMapResponse,
  ReportMeasure,
  ReportPeriod,
  ReportQuery
} from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import {
  useAuthenticated,
  useFloorSpacesQuery,
  useInvalidateReportAnswers,
  useResource,
  useResources
} from "@gettactic/hooks";
import { MapBuilderViewer } from "@gettactic/map-builder/src/MapBuilderViewer";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDayReplay } from "../hooks/useDayReplay";
import { useLiveMap } from "../hooks/useLiveMap";
import { useReportMap } from "../hooks/useReportMap";
import { useReportQuery } from "../hooks/useReportQuery";
import { formatValue } from "../lib/formatValue";
import { DeskEditor } from "./DeskEditor";
import { LegacySvgHeatmap } from "./LegacySvgHeatmap";
import { MapFrame } from "./MapFrame";
import { ReportSkeleton } from "./ReportSkeleton";

interface ReportMapViewProps {
  catalog: ReportCatalog;
  measure: string;
  floorId: string;
  period: ReportPeriod;
  /** Height of the map area. */
  height?: number;
  /** Start coloured by who may book each desk instead of by the measure. */
  initialMode?: "period" | "assignments";
  /** A desk picked elsewhere (a row in a list), drawn so it stands out. */
  highlightResourceId?: string | null;
}

/** Sequential ramps, light to dark; the "bad" ramp is for measures that should go down. */
export const HEAT_RAMPS: Record<ReportGoodWhen, string[]> = {
  up: ["#eaf2fc", "#c6dcf6", "#9cc2ee", "#6aa3e3", "#3d84d6", "#1c5cab"],
  down: ["#fdeeea", "#f9cfc4", "#f3a894", "#ec7f61", "#e2562f", "#b93a17"]
};
const EMPTY = "#eef0f2";

/** Diverging ramps for "what changed": better in green, worse in red. */
const BETTER_RAMP = ["#dcefe0", "#a9dcb5", "#6dc384", "#2f9e4f"];
const WORSE_RAMP = ["#fbe0dc", "#f5b3a9", "#ea7d6c", "#d94b3b"];

/** Colour for a change against the largest change on the floor. */
const deltaColor = (
  delta: number,
  maxAbs: number,
  measure: Pick<ReportMeasure, "good_when">
): string => {
  if (delta === 0 || maxAbs <= 0) {
    return EMPTY;
  }
  const isBetter = measure.good_when === "down" ? delta < 0 : delta > 0;
  const ramp = isBetter ? BETTER_RAMP : WORSE_RAMP;
  const step = Math.min(
    ramp.length - 1,
    Math.floor((Math.abs(delta) / maxAbs) * ramp.length)
  );
  return ramp[step];
};

/** The window of the same length that ends the day before `from`. */
const previousWindow = (from: string, to: string): ReportPeriod => {
  const start = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
  const prevEnd = new Date(start.getTime() - 86_400_000);
  const prevStart = new Date(prevEnd.getTime() - (days - 1) * 86_400_000);
  return {
    preset: "custom",
    from: prevStart.toISOString().slice(0, 10),
    to: prevEnd.toISOString().slice(0, 10)
  };
};

/** Live colours: arrived, booked but empty, free. */
const LIVE_COLORS: Record<ReportLiveState, string> = {
  present: "#1c5cab",
  expected: "#9cc2ee",
  free: EMPTY
};
const LIVE_LABELS: Record<ReportLiveState, string> = {
  present: "Checked in",
  expected: "Booked, not arrived",
  free: "Free"
};

/** Who may book a desk: the colours and words of the assignments view. */
const ACCESS_COLORS: Record<ReportAccessMode, string> = {
  dedicated: "#6f4fc7",
  restricted: "#e6a23c",
  open: "#8fd19e",
  unassigned: EMPTY
};
const ACCESS_LABELS: Record<ReportAccessMode, string> = {
  dedicated: "Dedicated",
  restricted: "Restricted",
  open: "Open",
  unassigned: "Unassigned"
};
const ACCESS_MODES: ReportAccessMode[] = [
  "dedicated",
  "restricted",
  "open",
  "unassigned"
];
/** A desk whose access rule names nobody, and the one picked from a list. */
const NEEDS_REVIEW_COLOR = "#d94b3b";
const HIGHLIGHT_COLOR = "#111827";
/** Only desks are assigned; rooms and parking stay neutral in that view. */
const DESK_TYPES = new Set(["desk", "workspace"]);

/** "Dedicated to Ana Silva", "Restricted to Marketing Team (Primary)"... */
const assignmentLabel = (resource: ReportMapResource): string => {
  switch (resource.access_mode) {
    case "dedicated": {
      const dedicated = resource.assigned_to
        ? `Dedicated to ${resource.assigned_to}`
        : "Dedicated to nobody · needs review";
      // Released while its owner is away: anyone, or named people, may book it.
      if (resource.released_until) {
        const to = resource.release === "assigned" ? "named people" : "anyone";
        return `${dedicated} · available to ${to} until ${resource.released_until}`;
      }
      return dedicated;
    }
    case "restricted":
      return resource.assigned_to
        ? `Restricted to ${resource.assigned_to}`
        : "Restricted";
    case "open":
      return "Open to anyone";
    default:
      return "Nobody may book it · needs review";
  }
};

/** Colour for a value in 0..max on a six-step ramp; zero stays neutral. */
export const heatColor = (
  value: number,
  max: number,
  measure: Pick<ReportMeasure, "good_when">
): string => {
  if (value <= 0 || max <= 0) {
    return EMPTY;
  }
  const ramp = HEAT_RAMPS[measure.good_when];
  const step = Math.min(
    ramp.length - 1,
    Math.floor((value / max) * ramp.length)
  );
  return ramp[step];
};

const MAP_RESOURCE_TYPES = [
  "desk",
  "workspace",
  "meeting_room",
  "parking_space",
  "parking_lot"
];

const REPLAY_STEP_MS = 650;
/** The working day an hour-by-hour replay walks through. */
const HOUR_FROM = 7;
const HOUR_TO = 20;

const toMinutes = (clock: string): number => {
  const [h, m] = clock.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

const hourLabel = (hour: number): string =>
  `${String(hour).padStart(2, "0")}:00`;

/** `YYYY-MM-DD` for every day from `from` to `to` inclusive. */
const daysBetween = (from: string, to: string): string[] => {
  const out: string[] = [];
  const cursor = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  while (cursor <= end && out.length < 400) {
    out.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
};

const dayLabel = (day: string): string =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC"
  });

interface HoverProps {
  resource: ReportMapResource;
  measure: ReportMeasure;
  workingDays: number;
  /** The same spot in the previous window, when comparing. */
  previous?: ReportMapResource;
  x: number;
  y: number;
}

const HoverCard: React.FC<HoverProps> = ({
  resource,
  measure,
  workingDays,
  previous,
  x,
  y
}) => (
  <div
    className="pointer-events-none absolute z-20 flex min-w-[170px] flex-col gap-1 rounded-lg bg-neutral-900 px-3 py-2 text-white text-xs shadow-xl"
    // Follows the cursor.
    style={{ left: x + 14, top: y + 14 }}
  >
    <span className="font-medium">{resource.name}</span>
    <span className="flex justify-between gap-3">
      <span className="text-neutral-300">{measure.label}</span>
      <span className="font-semibold tabular-nums">
        {formatValue(measure, resource.value)}
      </span>
    </span>
    {previous ? (
      <span className="flex justify-between gap-3 text-neutral-400">
        <span>Previous period</span>
        <span className="tabular-nums">
          {formatValue(measure, previous.value)}
        </span>
      </span>
    ) : null}
    <span className="flex justify-between gap-3 text-neutral-400">
      <span>Reserved</span>
      <span className="tabular-nums">
        {resource.reserved} / {workingDays} days
      </span>
    </span>
    <span className="flex justify-between gap-3 text-neutral-400">
      <span>Checked in</span>
      <span className="tabular-nums">{resource.checked_in}</span>
    </span>
    {resource.no_shows > 0 ? (
      <span className="flex justify-between gap-3 text-neutral-400">
        <span>Missed check-ins</span>
        <span className="tabular-nums">{resource.no_shows}</span>
      </span>
    ) : null}
  </div>
);

interface PinLayerProps {
  pins: ReportMapPin[];
  size: { w: number; h: number };
}

/**
 * Requests dropped on a point, drawn as soft blobs in the map's own
 * coordinates so overlapping ones read as a hot spot; a dot marks each pin.
 */
const PinLayer: React.FC<PinLayerProps> = ({ pins, size }) => {
  // Sized against the plan, not the screen: at fit scale a 4000-unit floor
  // is ~900px wide, so a blob must be a few hundred units to read at all.
  const radius = Math.max(size.w, size.h) * 0.06;
  const dot = Math.max(size.w, size.h) * 0.009;
  return (
    <svg
      className="pointer-events-none absolute top-0 left-0 z-10"
      width={size.w}
      height={size.h}
      viewBox={`0 0 ${size.w} ${size.h}`}
      aria-hidden
    >
      <defs>
        <radialGradient id="report-pin-heat">
          <stop offset="0%" stopColor="#e2562f" stopOpacity="0.7" />
          <stop offset="55%" stopColor="#e2562f" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#e2562f" stopOpacity="0" />
        </radialGradient>
      </defs>
      {pins.map((p, i) => (
        <circle
          key={`${p.x}-${p.y}-${i}`}
          cx={p.x}
          cy={p.y}
          r={radius}
          fill="url(#report-pin-heat)"
        />
      ))}
      {pins.map((p, i) => (
        <circle
          key={`dot-${p.x}-${p.y}-${i}`}
          cx={p.x}
          cy={p.y}
          r={dot}
          fill="#b93a17"
          stroke="#ffffff"
          strokeWidth={dot * 0.3}
        >
          <title>{`${p.title}${p.category ? ` · ${p.category}` : ""} · ${p.status}`}</title>
        </circle>
      ))}
    </svg>
  );
};

interface LiveBarProps {
  live: ReportLiveMapResponse | undefined;
  onClose: () => void;
}

/** The live legend: who is in, who is due, what is free, and when that was true. */
const LiveBar: React.FC<LiveBarProps> = ({ live, onClose }) => (
  <div className="flex flex-wrap items-center gap-4 rounded-lg bg-neutral-50 px-3 py-2 text-[12px]">
    <span className="flex items-center gap-1.5 font-medium text-neutral-800">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#1a9a3c] opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-[#1a9a3c]" />
      </span>
      Right now
    </span>
    {(["present", "expected", "free"] as ReportLiveState[]).map((state) => (
      <span key={state} className="flex items-center gap-1.5 text-neutral-600">
        <span
          className="h-2.5 w-2.5 rounded-[2px] border border-neutral-200"
          style={{ backgroundColor: LIVE_COLORS[state] }}
        />
        <span className="tabular-nums">{live ? live[state] : "…"}</span>
        {LIVE_LABELS[state].toLowerCase()}
      </span>
    ))}
    <span className="flex-1" />
    <span className="text-neutral-400">
      {live
        ? `as of ${new Date(live.as_of).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} · refreshes every minute`
        : "loading…"}
    </span>
    <button
      type="button"
      onClick={onClose}
      className="text-neutral-500 hover:text-neutral-900"
    >
      Back to the period
    </button>
  </div>
);

interface ReplayBarProps {
  days: string[];
  index: number;
  isPlaying: boolean;
  onIndexChange: (index: number) => void;
  onPlayingChange: (isPlaying: boolean) => void;
  /** Walk the picked day hour by hour instead of the period day by day. */
  isHourly: boolean;
  onHourlyChange: (isHourly: boolean) => void;
  onClose: () => void;
}

/** The day scrubber under the map: play through the period or drag to a day. */
const ReplayBar: React.FC<ReplayBarProps> = ({
  days,
  index,
  isPlaying,
  onIndexChange,
  onPlayingChange,
  isHourly,
  onHourlyChange,
  onClose
}) => (
  <div className="flex items-center gap-3 rounded-lg bg-neutral-50 px-3 py-2">
    <Button
      variant="outline"
      size="sm"
      aria-label={isPlaying ? "Pause" : "Play"}
      onClick={() => {
        if (!isPlaying && index >= days.length - 1) {
          onIndexChange(0);
        }
        onPlayingChange(!isPlaying);
      }}
    >
      {isPlaying ? "❚❚" : "▶"}
    </Button>
    <span className="w-[120px] shrink-0 text-[12px] text-neutral-700 tabular-nums">
      {days[index] ? dayLabel(days[index]) : ""}
    </span>
    <div className="flex-1">
      <Slider
        min={0}
        max={Math.max(0, days.length - 1)}
        step={1}
        value={[index]}
        onValueChange={([next]) => {
          onPlayingChange(false);
          onIndexChange(next ?? 0);
        }}
      />
    </div>
    <span className="text-[11px] text-neutral-400 tabular-nums">
      {index + 1} / {days.length}
    </span>
    <button
      type="button"
      onClick={() => {
        onPlayingChange(false);
        onHourlyChange(!isHourly);
      }}
      className={cn(
        "text-[12px] hover:text-neutral-900",
        isHourly ? "font-medium text-neutral-800" : "text-neutral-500"
      )}
    >
      {isHourly ? "Whole day" : "Hour by hour"}
    </button>
    <button
      type="button"
      onClick={onClose}
      className="text-[12px] text-neutral-500 hover:text-neutral-900"
    >
      Whole period
    </button>
  </div>
);

interface HourBarProps {
  hour: number;
  isPlaying: boolean;
  onHourChange: (hour: number) => void;
  onPlayingChange: (isPlaying: boolean) => void;
  /** How many resources are in each state at that hour. */
  counts: Record<ReportLiveState, number>;
}

/** The hour scrubber under the day scrubber: the picked day from 07:00 to 20:00. */
const HourBar: React.FC<HourBarProps> = ({
  hour,
  isPlaying,
  onHourChange,
  onPlayingChange,
  counts
}) => (
  <div className="flex flex-wrap items-center gap-3 rounded-lg bg-neutral-50 px-3 py-2">
    <Button
      variant="outline"
      size="sm"
      aria-label={isPlaying ? "Pause" : "Play"}
      onClick={() => {
        if (!isPlaying && hour >= HOUR_TO) {
          onHourChange(HOUR_FROM);
        }
        onPlayingChange(!isPlaying);
      }}
    >
      {isPlaying ? "❚❚" : "▶"}
    </Button>
    <span className="w-[120px] shrink-0 text-[12px] text-neutral-700 tabular-nums">
      {hourLabel(hour)}
    </span>
    <div className="min-w-[160px] flex-1">
      <Slider
        min={HOUR_FROM}
        max={HOUR_TO}
        step={1}
        value={[hour]}
        onValueChange={([next]) => {
          onPlayingChange(false);
          onHourChange(next ?? HOUR_FROM);
        }}
      />
    </div>
    {(["present", "expected", "free"] as ReportLiveState[]).map((state) => (
      <span
        key={state}
        className="flex items-center gap-1.5 text-[12px] text-neutral-600"
      >
        <span
          className="h-2.5 w-2.5 rounded-[2px] border border-neutral-200"
          style={{ backgroundColor: LIVE_COLORS[state] }}
        />
        <span className="tabular-nums">{counts[state]}</span>
        {LIVE_LABELS[state].toLowerCase()}
      </span>
    ))}
  </div>
);

/**
 * What the map shows for one replay day: the per-resource values of that
 * day (usage) or the pins filed up to it (requests).
 */
const replayFrame = (
  data: ReportMapResponse,
  day: string
): { values: Record<string, number> | null; pins: ReportMapPin[] } => {
  const values = data.days?.find((d) => d.date === day)?.values ?? null;
  return { values, pins: data.pins.filter((p) => p.date <= day) };
};

/**
 * The floor plan with every desk, room and parking spot coloured by the
 * measure over the period. Hover a spot for its numbers; replay walks the
 * period day by day. Colours are the measure's data, not availability —
 * the map's own state dots are painted over.
 */
export const ReportMapView: React.FC<ReportMapViewProps> = ({
  catalog,
  measure,
  floorId,
  period,
  height = 520,
  initialMode = "period",
  highlightResourceId = null
}) => {
  const [isReplay, setIsReplay] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [isDelta, setIsDelta] = useState(false);
  const [isAssignments, setIsAssignments] = useState(
    initialMode === "assignments"
  );
  // Shade the spaces drawn on the map (rooms, zone areas) by the measure
  // instead of the desks, to see which parts of the floor carry the load.
  const [isSpaces, setIsSpaces] = useState(false);
  // The space shading belongs to the period view; live, replay, delta and
  // assignments paint the desks themselves, so it steps aside there and
  // comes back with the period view.
  const isPeriodView = !isLive && !isReplay && !isDelta && !isAssignments;
  // The floor's spaces are known up front, so By space is only offered
  // when there is something to shade.
  const { data: floorSpaces } = useFloorSpacesQuery(
    isPeriodView ? floorId : null
  );
  const hasSpaces = (floorSpaces?.length ?? 0) > 0;
  const isSpacesShown = isSpaces && isPeriodView && hasSpaces;
  const spacesQuery = useMemo<ReportQuery | null>(
    () =>
      isSpacesShown
        ? {
            measure,
            breakdown: ["space"],
            filters: [{ dimension: "floor", in: [floorId] }],
            period
          }
        : null,
    [isSpacesShown, measure, floorId, period]
  );
  const { data: spacesResult } = useReportQuery(spacesQuery);
  const [dayIndex, setDayIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  // The picked day walked hour by hour, from the reservations themselves.
  const [isHourly, setIsHourly] = useState(false);
  const [hour, setHour] = useState(9);
  const [isHourPlaying, setIsHourPlaying] = useState(false);
  const { data: live } = useLiveMap(floorId, isLive);
  const { data, isLoading, isError } = useReportMap({
    measure,
    floor_id: floorId,
    period,
    ...(isReplay && measure !== "requests" ? { by_day: true } : {})
  });
  const { data: previous } = useReportMap(
    isDelta && data
      ? {
          measure,
          floor_id: floorId,
          period: previousWindow(data.from, data.to)
        }
      : null
  );
  const previousById = useMemo(
    () => new Map((previous?.resources ?? []).map((r) => [r.resource_id, r])),
    [previous]
  );
  const officeId = data?.floor.office_id ?? null;
  // The floor itself carries the map; its children are the spots to label.
  const { resource: floor, loading: isFloorLoading } = useResource(floorId);
  const { resources } = useResources({
    officeId: officeId ?? "",
    types: MAP_RESOURCE_TYPES,
    parentId: floorId
  });
  const [hovered, setHovered] = useState<string | null>(null);
  const [cursor, setCursor] = useState({ x: 0, y: 0 });
  // The desk being edited in the office settings' panel, opened from here.
  const [editingId, setEditingId] = useState<string | null>(null);
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const invalidateAnswers = useInvalidateReportAnswers();
  const [contentSize, setContentSize] = useState<{
    w: number;
    h: number;
  } | null>(null);
  const handleContentSize = useCallback(
    (size: { w: number; h: number }) => setContentSize(size),
    []
  );

  const days = useMemo(
    () => (data ? daysBetween(data.from, data.to) : []),
    [data]
  );
  const day = isReplay ? days[Math.min(dayIndex, days.length - 1)] : undefined;
  const frame = data && day ? replayFrame(data, day) : null;
  const isHourReplay = isReplay && isHourly && !!day;
  const { data: replay } = useDayReplay(floorId, day, isHourReplay);
  // Every resource's state at the picked hour: checked in, booked, or free.
  const hourStates = useMemo(() => {
    const out: Record<string, ReportLiveState> = {};
    // Only the picked day's own answer colours the map.
    if (!replay || replay.date !== day) {
      return out;
    }
    const minute = hour * 60;
    for (const slot of replay.slots) {
      const start = toMinutes(slot.start);
      // A slot that ends past midnight runs to the end of this day's clock.
      const end = toMinutes(slot.end) > start ? toMinutes(slot.end) : 24 * 60;
      if (start > minute || minute >= end) {
        continue;
      }
      if (slot.checked_in || out[slot.resource_id] !== "present") {
        out[slot.resource_id] = slot.checked_in ? "present" : "expected";
      }
    }
    return out;
  }, [replay, hour, day]);

  useEffect(() => {
    if (!isPlaying) {
      return;
    }
    const timer = window.setInterval(() => {
      setDayIndex((i) => {
        if (i >= days.length - 1) {
          setIsPlaying(false);
          return i;
        }
        return i + 1;
      });
    }, REPLAY_STEP_MS);
    return () => window.clearInterval(timer);
  }, [isPlaying, days.length]);

  useEffect(() => {
    if (!isHourPlaying) {
      return;
    }
    const timer = window.setInterval(() => {
      setHour((h) => {
        if (h >= HOUR_TO) {
          setIsHourPlaying(false);
          return h;
        }
        return h + 1;
      });
    }, REPLAY_STEP_MS);
    return () => window.clearInterval(timer);
  }, [isHourPlaying]);

  // The period's max keeps the scale steady while replaying; a day's max
  // would make every day look equally busy.
  const max = useMemo(() => {
    const whole = Math.max(...(data?.resources.map((r) => r.value) ?? [0]), 0);
    if (!frame?.values) {
      return whole;
    }
    const perDay = (data?.days ?? []).flatMap((d) => Object.values(d.values));
    return Math.max(...perDay, 0);
  }, [data, frame]);
  const spaceColors = useMemo(() => {
    if (!isSpacesShown || !spacesResult || !floorSpaces) return undefined;
    const shapeByRegion = new Map(floorSpaces.map((s) => [s.id, s.shape_id]));
    const max = Math.max(...spacesResult.rows.map((r) => r.value), 0);
    const out: Record<string, string> = {};
    for (const space of floorSpaces) {
      out[space.shape_id] = EMPTY;
    }
    for (const row of spacesResult.rows) {
      const shapeId = shapeByRegion.get(row.keys[0]);
      if (shapeId) {
        out[shapeId] = heatColor(row.value, max, spacesResult.meta.measure);
      }
    }
    return out;
  }, [isSpacesShown, spacesResult, floorSpaces]);

  const colors = useMemo(() => {
    const out: Record<string, string> = {};
    // Desks blank out only once the space shading is there to replace them;
    // until the queries resolve they keep their own colours.
    if (isSpacesShown && spaceColors) {
      for (const r of data?.resources ?? []) {
        out[r.resource_id] = EMPTY;
      }
      return out;
    }
    if (isLive) {
      for (const r of live?.resources ?? []) {
        out[r.resource_id] = LIVE_COLORS[r.state];
      }
      return out;
    }
    if (isHourReplay) {
      for (const r of data?.resources ?? []) {
        out[r.resource_id] = LIVE_COLORS[hourStates[r.resource_id] ?? "free"];
      }
      return out;
    }
    if (isAssignments) {
      for (const r of data?.resources ?? []) {
        out[r.resource_id] = !DESK_TYPES.has(r.type)
          ? EMPTY
          : r.health === "needs_review"
            ? NEEDS_REVIEW_COLOR
            : ACCESS_COLORS[r.access_mode];
      }
      if (highlightResourceId) {
        out[highlightResourceId] = HIGHLIGHT_COLOR;
      }
      return out;
    }
    if (isDelta && data && previous) {
      const deltas = data.resources.map(
        (r) => r.value - (previousById.get(r.resource_id)?.value ?? 0)
      );
      const maxAbs = Math.max(...deltas.map(Math.abs), 0);
      data.resources.forEach((r, i) => {
        out[r.resource_id] = deltaColor(deltas[i], maxAbs, data.measure);
      });
      return out;
    }
    const goodWhen = data?.measure ?? { good_when: "up" as const };
    for (const r of data?.resources ?? []) {
      const value = frame?.values
        ? (frame.values[r.resource_id] ?? 0)
        : r.value;
      out[r.resource_id] = heatColor(value, max, goodWhen);
    }
    return out;
  }, [
    data,
    max,
    frame,
    isLive,
    live,
    isHourReplay,
    hourStates,
    isAssignments,
    isSpacesShown,
    spaceColors,
    highlightResourceId,
    isDelta,
    previous,
    previousById
  ]);
  const hourCounts = useMemo(() => {
    const counts: Record<ReportLiveState, number> = {
      present: 0,
      expected: 0,
      free: 0
    };
    for (const r of data?.resources ?? []) {
      counts[hourStates[r.resource_id] ?? "free"]++;
    }
    return counts;
  }, [data, hourStates]);
  const liveById = useMemo(
    () => new Map((live?.resources ?? []).map((r) => [r.resource_id, r])),
    [live]
  );
  const byId = useMemo(
    () => new Map((data?.resources ?? []).map((r) => [r.resource_id, r])),
    [data]
  );

  if (isError) {
    return (
      <p className="py-16 text-center text-[13px] text-semantic-error-foreground">
        This floor couldn't be drawn.
      </p>
    );
  }
  if (isLoading || isFloorLoading || !data || !resources) {
    return <ReportSkeleton kind="map" />;
  }
  if (!floor) {
    return (
      <p className="py-16 text-center text-neutral-400 text-sm">
        This floor couldn't be loaded.
      </p>
    );
  }
  if (!floor.map_data && !floor.map_url) {
    return (
      <p className="py-16 text-center text-neutral-400 text-sm">
        {floor.name} has no floor plan yet.
      </p>
    );
  }

  const measureDef = data.measure;
  const hoveredResource = hovered ? byId.get(hovered) : undefined;
  // Who may change a desk here is who may in the office settings or on
  // the map: settings access, or the resource manager permission. The
  // click lands on the plan's own shape for the desk.
  const mayManageResources =
    !!authenticatedUser.permissions.settings ||
    !!authenticatedUser.user?.organization_user?.permissions?.includes(
      "resource:manage"
    );
  const canEditDesks = isAssignments && !!officeId && mayManageResources;
  const pickDesk = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!canEditDesks) {
      return;
    }
    const group = (event.target as Element).closest('g[id^="reso_"]');
    if (group && byId.has(group.id)) {
      setEditingId(group.id);
    }
  };
  const ramp = HEAT_RAMPS[measureDef.good_when];
  const floorMeasure =
    catalog.measures.find((m) => m.key === measure) ?? measureDef;
  const pins = isLive || isAssignments ? [] : frame ? frame.pins : data.pins;
  const hoveredLive = hovered ? liveById.get(hovered) : undefined;
  const desks = data.resources.filter((r) => DESK_TYPES.has(r.type));

  return (
    <div className="flex flex-col gap-2">
      <MapFrame
        height={height}
        contentKey={`${floor.id}-${floor.map_data ? "builder" : "legacy"}`}
        onMouseMove={(x, y) => setCursor({ x, y })}
        onContentSize={handleContentSize}
        overlay={
          isLive && hoveredLive ? (
            <div
              className="pointer-events-none absolute z-20 rounded-lg bg-neutral-900 px-3 py-2 text-white text-xs shadow-xl"
              // Follows the cursor.
              style={{ left: cursor.x + 14, top: cursor.y + 14 }}
            >
              <span className="font-medium">{hoveredLive.name}</span>
              <span className="text-neutral-300">
                {" "}
                · {LIVE_LABELS[hoveredLive.state]}
              </span>
            </div>
          ) : hoveredResource && isHourReplay ? (
            <div
              className="pointer-events-none absolute z-20 rounded-lg bg-neutral-900 px-3 py-2 text-white text-xs shadow-xl"
              // Follows the cursor.
              style={{ left: cursor.x + 14, top: cursor.y + 14 }}
            >
              <span className="font-medium">{hoveredResource.name}</span>
              <span className="text-neutral-300">
                {" "}
                ·{" "}
                {LIVE_LABELS[hourStates[hoveredResource.resource_id] ?? "free"]}{" "}
                at {hourLabel(hour)}
              </span>
            </div>
          ) : hoveredResource && isAssignments ? (
            <div
              className="pointer-events-none absolute z-20 max-w-[280px] rounded-lg bg-neutral-900 px-3 py-2 text-white text-xs shadow-xl"
              // Follows the cursor.
              style={{ left: cursor.x + 14, top: cursor.y + 14 }}
            >
              <span className="font-medium">{hoveredResource.name}</span>
              <span className="text-neutral-300">
                {" "}
                · {assignmentLabel(hoveredResource)}
              </span>
            </div>
          ) : hoveredResource && !frame && !isLive ? (
            <HoverCard
              resource={hoveredResource}
              measure={floorMeasure}
              workingDays={data.working_days}
              previous={
                isDelta
                  ? previousById.get(hoveredResource.resource_id)
                  : undefined
              }
              x={cursor.x}
              y={cursor.y}
            />
          ) : null
        }
      >
        {/* biome-ignore lint/a11y/noStaticElementInteractions: the plan's own shapes are the targets; a click on a desk opens its editor */}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: the desks are reached from the lists on the board by keyboard */}
        <div
          className={cn("relative", canEditDesks && "cursor-pointer")}
          onClick={pickDesk}
        >
          {floor.map_data ? (
            <MapBuilderViewer
              floor={floor}
              resources={resources.elements}
              useSvg
              resourceColors={colors}
              zoneColors={spaceColors}
              hideZoneLegend
              highlightResource={(resourceId) => setHovered(resourceId)}
            />
          ) : (
            <LegacySvgHeatmap
              mapUrl={floor.map_url}
              colors={colors}
              onHover={setHovered}
              isPickable={canEditDesks}
            />
          )}
          {pins.length > 0 && contentSize ? (
            <PinLayer pins={pins} size={contentSize} />
          ) : null}
        </div>
      </MapFrame>
      {officeId && editingId ? (
        <DeskEditor
          officeId={officeId}
          resourceId={editingId}
          onClose={() => {
            setEditingId(null);
            // Whatever the panel changed, the map reads it again.
            void invalidateAnswers();
          }}
          onSaved={() => {
            void invalidateAnswers();
          }}
        />
      ) : null}
      {isLive ? <LiveBar live={live} onClose={() => setIsLive(false)} /> : null}
      {isReplay && !isLive && days.length > 0 ? (
        <ReplayBar
          days={days}
          index={Math.min(dayIndex, days.length - 1)}
          isPlaying={isPlaying}
          onIndexChange={setDayIndex}
          onPlayingChange={setIsPlaying}
          isHourly={isHourly}
          onHourlyChange={(next) => {
            setIsHourPlaying(false);
            setIsHourly(next);
          }}
          onClose={() => {
            setIsPlaying(false);
            setIsHourPlaying(false);
            setIsHourly(false);
            setIsReplay(false);
          }}
        />
      ) : null}
      {isHourReplay ? (
        <HourBar
          hour={hour}
          isPlaying={isHourPlaying}
          onHourChange={setHour}
          onPlayingChange={setIsHourPlaying}
          counts={hourCounts}
        />
      ) : null}
      <div className="flex items-center gap-3 text-[11px] text-neutral-500">
        <span>{data.floor.name}</span>
        <span className="text-neutral-300">·</span>
        <span>
          {isAssignments
            ? "who may book each desk"
            : day
              ? `${dayLabel(day)}${isHourReplay ? ` · ${hourLabel(hour)}` : ""}`
              : `${data.from} – ${data.to}`}
        </span>
        {!isReplay && !isLive && !isAssignments && days.length > 1 ? (
          <>
            <span className="text-neutral-300">·</span>
            <button
              type="button"
              onClick={() => {
                // The replay shows one day at a time; the comparison with
                // the previous period is a whole-period view.
                setIsDelta(false);
                setDayIndex(0);
                setIsReplay(true);
                setIsPlaying(true);
              }}
              className="text-primary hover:underline"
            >
              ▶ Replay day by day
            </button>
          </>
        ) : null}
        {!isLive && !isAssignments && measure !== "requests" ? (
          <>
            <span className="text-neutral-300">·</span>
            <button
              type="button"
              onClick={() => {
                setIsPlaying(false);
                setIsReplay(false);
                setIsDelta(false);
                setIsLive(true);
              }}
              className="text-primary hover:underline"
            >
              ● Right now
            </button>
          </>
        ) : null}
        {!isLive && !isReplay && !isAssignments ? (
          <>
            <span className="text-neutral-300">·</span>
            <button
              type="button"
              onClick={() => setIsDelta((v) => !v)}
              className={cn(
                "hover:underline",
                isDelta ? "font-medium text-neutral-800" : "text-primary"
              )}
            >
              {isDelta ? "Back to the period" : "△ What changed"}
            </button>
          </>
        ) : null}
        {isPeriodView && hasSpaces ? (
          <>
            <span className="text-neutral-300">·</span>
            <button
              type="button"
              onClick={() => setIsSpaces((v) => !v)}
              className={cn(
                "hover:underline",
                isSpaces ? "font-medium text-neutral-800" : "text-primary"
              )}
            >
              {isSpaces ? "Back to desks" : "▣ By space"}
            </button>
          </>
        ) : null}
        {!isLive &&
        !isReplay &&
        !isDelta &&
        !isSpacesShown &&
        desks.length > 0 ? (
          <>
            <span className="text-neutral-300">·</span>
            <button
              type="button"
              onClick={() => setIsAssignments((v) => !v)}
              className={cn(
                "hover:underline",
                isAssignments ? "font-medium text-neutral-800" : "text-primary"
              )}
            >
              {isAssignments ? "Back to the period" : "⌂ Who sits where"}
            </button>
          </>
        ) : null}
        {canEditDesks ? (
          <>
            <span className="text-neutral-300">·</span>
            <span>Click a desk to change who may book it</span>
          </>
        ) : null}
        <span className="flex-1" />
        {isAssignments ? (
          <>
            {ACCESS_MODES.map((mode) => (
              <span key={mode} className="flex items-center gap-1.5">
                <span
                  className="h-2.5 w-2.5 rounded-[2px] border border-neutral-200"
                  style={{ backgroundColor: ACCESS_COLORS[mode] }}
                />
                <span className="tabular-nums">
                  {desks.filter((r) => r.access_mode === mode).length}
                </span>
                {ACCESS_LABELS[mode].toLowerCase()}
              </span>
            ))}
            <span className="flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 rounded-[2px] border border-neutral-200"
                style={{ backgroundColor: NEEDS_REVIEW_COLOR }}
              />
              <span className="tabular-nums">
                {desks.filter((r) => r.health === "needs_review").length}
              </span>
              needs review
            </span>
            {highlightResourceId ? (
              <span className="flex items-center gap-1.5">
                <span
                  className="h-2.5 w-2.5 rounded-[2px]"
                  style={{ backgroundColor: HIGHLIGHT_COLOR }}
                />
                the desk picked
              </span>
            ) : null}
          </>
        ) : isDelta ? (
          <>
            <span>worse</span>
            <span className="flex overflow-hidden rounded">
              {[...WORSE_RAMP].reverse().map((c) => (
                <span
                  key={c}
                  className="h-2.5 w-4"
                  style={{ backgroundColor: c }}
                />
              ))}
              <span className="h-2.5 w-4" style={{ backgroundColor: EMPTY }} />
              {BETTER_RAMP.map((c) => (
                <span
                  key={c}
                  className="h-2.5 w-4"
                  style={{ backgroundColor: c }}
                />
              ))}
            </span>
            <span>better</span>
            <span className="text-neutral-400">
              {previous
                ? ` · vs ${previous.from} – ${previous.to}`
                : " · loading the previous period…"}
            </span>
          </>
        ) : (
          <>
            <span>0</span>
            <span className="flex overflow-hidden rounded">
              {ramp.map((c) => (
                <span
                  key={c}
                  className="h-2.5 w-5"
                  style={{ backgroundColor: c }}
                />
              ))}
            </span>
            <span className="tabular-nums">
              {formatValue(floorMeasure, max)}
            </span>
          </>
        )}
        {isAssignments ? null : (
          <span className="text-neutral-400">
            {measure === "requests"
              ? ` · ${data.resources.reduce((n, r) => n + r.reserved, 0)} on desks and rooms · ${pins.length} pinned${data.unplaced > 0 ? ` · ${data.unplaced} floor only` : ""}`
              : frame?.values
                ? ` · ${Object.values(frame.values).filter((v) => v > 0).length} of ${data.resources.length} used that day`
                : ` · ${data.resources.filter((r) => r.value > 0).length} of ${data.resources.length} used`}
          </span>
        )}
      </div>
    </div>
  );
};
